import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Save,
  Plus,
  X,
  Check,
  Calendar,
  ExternalLink,
  Pencil,
  Trash2,
  Settings2,
  Download,
  Printer,
  CheckSquare,
  StickyNote,
  LayoutGrid,
  List,
} from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from '../components/editor';
import { apiGet, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { tiptapToMarkdown, downloadMarkdown, printAsPdf } from '../lib/export';
import { ViewGridLayout } from '../components/views/ViewGridLayout';
import {
  parseLayoutConfig,
  generateDefaultLayout,
  reconcileLayout,
  type LayoutConfig,
  type GridCardConfig,
} from '../components/views/layoutUtils';

interface Tag {
  id: string;
  name: string;
  color: string;
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'meeting_instance', label: 'Meetings', icon: Calendar },
  { value: 'task', label: 'Tasks', icon: CheckSquare },
  { value: 'note', label: 'Notes', icon: StickyNote },
] as const;

interface ViewDetail {
  id: string;
  title: string;
  annotations: string | null;
  snapshot_date: string | null;
  entity_types: string;
  layout_config: string | null;
  tag_ids: string[];
  scope_ids: string[];
  tags: Tag[];
  created_at: string;
  updated_at: string;
}

interface ViewContentItem {
  source_type: 'meeting_instance' | 'task' | 'note';
  source_id: string;
  title: string;
  description?: string | null;
  tag_name: string;
  tag_id: string;
  tag_color: string;
  // Meeting-specific
  meeting_date?: string;
  series_id?: string;
  series_name?: string;
  tagged_text?: string;
  block_id?: string | null;
  note_tag_id?: string;
  // Task-specific
  status?: string;
  priority?: string;
  // Note-specific
  content?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Annotation: a user-written rich text section pinned between aggregated items
interface Annotation {
  id: string;
  after_item_id: string | null; // null = at the top, or the note_tag_id it comes after
  content: JSONContent;
}

export function ViewDetailPage() {
  const { docId } = useParams<{ docId: string }>();
  const toast = useToast();
  const [view, setView] = useState<ViewDetail | null>(null);
  const [contentItems, setContentItems] = useState<ViewContentItem[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig | null>(null);

  // Debounced layout save
  const layoutSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function getEntityTypes(): string[] {
    if (!view) return ['meeting_instance'];
    try {
      const parsed = JSON.parse(view.entity_types);
      return Array.isArray(parsed) ? parsed : ['meeting_instance'];
    } catch {
      return ['meeting_instance'];
    }
  }

  const isGridMode = layoutConfig?.mode === 'grid';

  async function load() {
    if (!docId) return;
    setLoading(true);
    try {
      const [viewData, contentData] = await Promise.all([
        apiGet<ViewDetail>(`/views/${docId}`),
        apiGet<ViewContentItem[]>(`/views/${docId}/content`),
      ]);
      setView(viewData);
      setContentItems(contentData);
      setTitleDraft(viewData.title);

      // Parse layout config
      setLayoutConfig(parseLayoutConfig(viewData.layout_config));

      // Parse existing annotations from JSON
      if (viewData.annotations) {
        try {
          const parsed = JSON.parse(viewData.annotations) as Annotation[];
          setAnnotations(parsed);
        } catch {
          setAnnotations([]);
        }
      } else {
        setAnnotations([]);
      }
    } catch (err) {
      console.error('Failed to load view:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [docId]);

  // Save layout config (debounced)
  const saveLayoutConfig = useCallback((config: LayoutConfig | null) => {
    if (!docId) return;
    if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
    layoutSaveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await apiPatch(`/views/${docId}`, {
          layout_config: config ? JSON.stringify(config) : null,
        });
        setLastSaved(new Date().toLocaleTimeString());
      } catch (err) {
        console.error('Failed to save layout:', err);
        toast.error('Failed to save layout');
      } finally {
        setSaving(false);
      }
    }, 500);
  }, [docId, toast]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
    };
  }, []);

  const saveAnnotations = useCallback(async (updatedAnnotations: Annotation[]) => {
    if (!docId) return;
    setSaving(true);
    try {
      await apiPatch(`/views/${docId}`, {
        annotations: JSON.stringify(updatedAnnotations),
      });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save annotations:', err);
      toast.error('Failed to save annotations');
    } finally {
      setSaving(false);
    }
  }, [docId, toast]);

  async function handleTitleSave() {
    if (!docId || !titleDraft.trim()) return;
    setSaving(true);
    try {
      const updated = await apiPatch<ViewDetail>(`/views/${docId}`, {
        title: titleDraft.trim(),
      });
      setView(updated);
      setEditingTitle(false);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save title:', err);
      toast.error('Failed to save title');
    } finally {
      setSaving(false);
    }
  }

  async function handleTagsUpdate(tagIds: string[]) {
    if (!docId) return;
    setSaving(true);
    try {
      const updated = await apiPatch<ViewDetail>(`/views/${docId}`, {
        tag_ids: tagIds,
      });
      setView(updated);
      // Reload content with new tags
      const contentData = await apiGet<ViewContentItem[]>(`/views/${docId}/content`);
      setContentItems(contentData);
      setShowTagEditor(false);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to update tags:', err);
      toast.error('Failed to update tags');
    } finally {
      setSaving(false);
    }
  }

  async function handleEntityTypeToggle(type: string) {
    if (!docId || !view) return;
    const current = getEntityTypes();
    let updated: string[];
    if (current.includes(type)) {
      // Don't allow removing the last type
      if (current.length <= 1) return;
      updated = current.filter(t => t !== type);
    } else {
      updated = [...current, type];
    }

    setSaving(true);
    try {
      const updatedView = await apiPatch<ViewDetail>(`/views/${docId}`, {
        entity_types: updated,
      });
      setView(updatedView);
      // Reload content with new entity types
      const contentData = await apiGet<ViewContentItem[]>(`/views/${docId}/content`);
      setContentItems(contentData);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to update entity types:', err);
      toast.error('Failed to update entity types');
    } finally {
      setSaving(false);
    }
  }

  function addAnnotation(afterItemId: string | null) {
    const newAnnotation: Annotation = {
      id: crypto.randomUUID(),
      after_item_id: afterItemId,
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
    };
    const updated = [...annotations, newAnnotation];
    setAnnotations(updated);
    saveAnnotations(updated);
  }

  function updateAnnotation(id: string, content: JSONContent) {
    const updated = annotations.map((a) =>
      a.id === id ? { ...a, content } : a
    );
    setAnnotations(updated);
    saveAnnotations(updated);
  }

  function deleteAnnotation(id: string) {
    const updated = annotations.filter((a) => a.id !== id);
    setAnnotations(updated);
    saveAnnotations(updated);

    // Also remove from layout if in grid mode
    if (layoutConfig && layoutConfig.mode === 'grid') {
      const updatedCards = layoutConfig.cards.filter(c => !(c.type === 'annotation' && c.ref_id === id));
      const newConfig = { ...layoutConfig, cards: updatedCards };
      setLayoutConfig(newConfig);
      saveLayoutConfig(newConfig);
    }
  }

  // Computed data
  const meetingItems = useMemo(() => contentItems.filter(i => i.source_type === 'meeting_instance'), [contentItems]);
  const taskItems = useMemo(() => contentItems.filter(i => i.source_type === 'task'), [contentItems]);
  const noteItems = useMemo(() => contentItems.filter(i => i.source_type === 'note'), [contentItems]);
  const meetingGroups = useMemo(() => groupByMeeting(meetingItems), [meetingItems]);

  // Reconcile layout when content changes
  useEffect(() => {
    if (!layoutConfig || layoutConfig.mode !== 'grid') return;
    const reconciled = reconcileLayout(
      layoutConfig,
      meetingGroups.map(g => ({ meeting_instance_id: g.meeting_instance_id })),
      taskItems,
      noteItems,
      annotations,
    );
    // Only update if cards changed
    if (JSON.stringify(reconciled.cards) !== JSON.stringify(layoutConfig.cards)) {
      setLayoutConfig(reconciled);
      saveLayoutConfig(reconciled);
    }
  }, [contentItems, annotations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle between list and grid mode
  function handleModeToggle() {
    if (isGridMode) {
      // Switch to list
      const newConfig: LayoutConfig = { ...layoutConfig!, mode: 'list' };
      setLayoutConfig(newConfig);
      saveLayoutConfig(newConfig);
    } else {
      // Switch to grid — generate default layout if none exists
      let newConfig: LayoutConfig;
      if (layoutConfig) {
        newConfig = { ...layoutConfig, mode: 'grid' };
      } else {
        newConfig = generateDefaultLayout(
          meetingGroups.map(g => ({ meeting_instance_id: g.meeting_instance_id })),
          taskItems,
          noteItems,
          annotations,
        );
      }
      setLayoutConfig(newConfig);
      saveLayoutConfig(newConfig);
    }
  }

  // Grid handlers
  function handleGridReorder(cards: GridCardConfig[]) {
    if (!layoutConfig) return;
    const newConfig = { ...layoutConfig, cards };
    setLayoutConfig(newConfig);
    saveLayoutConfig(newConfig);
  }

  function handleGridToggleSpan(cardId: string) {
    if (!layoutConfig) return;
    const newCards = layoutConfig.cards.map(c =>
      c.id === cardId ? { ...c, span: (c.span === 1 ? 2 : 1) as 1 | 2 } : c
    );
    const newConfig = { ...layoutConfig, cards: newCards };
    setLayoutConfig(newConfig);
    saveLayoutConfig(newConfig);
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!view) {
    return <div className="h-full overflow-auto p-3 sm:p-6"><div className="text-text-muted text-center py-16">View not found.</div></div>;
  }

  const entityTypes = getEntityTypes();

  // Build the interleaved view: annotations placed after specific items (list mode only)
  const annotationsByPosition = new Map<string | null, Annotation[]>();
  for (const ann of annotations) {
    const key = ann.after_item_id;
    if (!annotationsByPosition.has(key)) annotationsByPosition.set(key, []);
    annotationsByPosition.get(key)!.push(ann);
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className={isGridMode ? 'max-w-6xl mx-auto' : 'max-w-4xl mx-auto'}>
      {/* Breadcrumb */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/views"
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} />
          Views
        </Link>
        <div className="flex items-center gap-2">
          {lastSaved && (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Save size={12} />
              Saved at {lastSaved}
            </span>
          )}
          {saving && (
            <span className="text-xs text-accent">Saving...</span>
          )}

          {/* Mode toggle */}
          <div className="flex items-center border border-border rounded-md overflow-hidden no-print">
            <button
              onClick={() => { if (isGridMode) handleModeToggle(); }}
              className={`p-1.5 transition-colors ${
                !isGridMode
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
              }`}
              title="List view"
            >
              <List size={14} />
            </button>
            <button
              onClick={() => { if (!isGridMode) handleModeToggle(); }}
              className={`p-1.5 transition-colors ${
                isGridMode
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
              }`}
              title="Grid view"
            >
              <LayoutGrid size={14} />
            </button>
          </div>

          <button
            onClick={() => {
              // Build markdown from annotations + aggregated content
              let md = `# ${view!.title}\n\n`;
              for (const ann of annotations) {
                md += tiptapToMarkdown(ann.content) + '\n';
              }
              for (const item of meetingItems) {
                md += `- ${item.tagged_text} [${item.tag_name}]\n`;
              }
              for (const item of taskItems) {
                md += `- [${item.status}] ${item.title} [${item.tag_name}]\n`;
              }
              for (const item of noteItems) {
                md += `- ${item.title} [${item.tag_name}]\n`;
              }
              downloadMarkdown(md, view!.title);
            }}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors no-print"
            title="Export as Markdown"
          >
            <Download size={14} />
          </button>
          <button
            onClick={printAsPdf}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors no-print"
            title="Print / Save as PDF"
          >
            <Printer size={14} />
          </button>
        </div>
      </div>

      {/* Header: editable title */}
      <div className="mb-4">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave();
                if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(view.title); }
              }}
              autoFocus
              className="text-2xl font-bold bg-bg-input border border-border rounded-md px-2 py-1 text-text-primary focus:outline-none focus:border-accent flex-1"
            />
            <button onClick={handleTitleSave} className="p-1 text-success hover:text-success/80">
              <Check size={20} />
            </button>
            <button onClick={() => { setEditingTitle(false); setTitleDraft(view.title); }} className="p-1 text-text-muted hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <h1 className="text-2xl font-bold text-text-primary">{view.title}</h1>
            <button
              onClick={() => setEditingTitle(true)}
              className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-text-primary transition-opacity"
            >
              <Pencil size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Entity type toggles */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-text-muted uppercase tracking-wider">Sources:</span>
        {ENTITY_TYPE_OPTIONS.map((opt) => {
          const active = entityTypes.includes(opt.value);
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              onClick={() => handleEntityTypeToggle(opt.value)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                active
                  ? 'bg-accent/10 border-accent text-accent'
                  : 'border-border text-text-muted hover:text-text-secondary hover:border-border-hover'
              }`}
            >
              <Icon size={12} />
              {opt.label} {active ? '✓' : ''}
            </button>
          );
        })}
      </div>

      {/* Tag filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <span className="text-xs text-text-muted uppercase tracking-wider">Filters:</span>
        {view.tags.map((tag) => (
          <span
            key={tag.id}
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: tag.color + '20', color: tag.color }}
          >
            {tag.name}
          </span>
        ))}
        {view.tags.length === 0 && (
          <span className="text-xs text-text-muted italic">No tag filters set</span>
        )}
        <button
          onClick={() => setShowTagEditor(true)}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-accent px-2 py-0.5 rounded-md hover:bg-bg-hover transition-colors"
        >
          <Settings2 size={12} /> Edit Tags
        </button>
      </div>

      {/* Content area */}
      {contentItems.length === 0 && annotations.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <p className="text-sm">
            {view.tags.length === 0
              ? 'Add tag filters to start aggregating content.'
              : 'No tagged content found. Tag content in your meetings, tasks, or notes to see it here.'}
          </p>
        </div>
      ) : isGridMode && layoutConfig ? (
        /* ── Grid mode ── */
        <ViewGridLayout
          cards={layoutConfig.cards}
          meetingGroups={meetingGroups}
          taskItems={taskItems}
          noteItems={noteItems}
          annotations={annotations}
          onReorder={handleGridReorder}
          onToggleSpan={handleGridToggleSpan}
          onUpdateAnnotation={updateAnnotation}
          onDeleteAnnotation={deleteAnnotation}
        />
      ) : (
        /* ── List mode ── */
        <div className="space-y-3">
          {/* Annotations at the top (before any aggregated content) */}
          <AddAnnotationButton onClick={() => addAnnotation(null)} label="Add note at top" />
          {(annotationsByPosition.get(null) ?? []).map((ann) => (
            <AnnotationBlock
              key={ann.id}
              annotation={ann}
              onUpdate={updateAnnotation}
              onDelete={deleteAnnotation}
            />
          ))}

          {/* Meeting groups */}
          {meetingGroups.map((group) => (
            <div key={group.key} className="space-y-2">
              {/* Meeting group header */}
              <div className="flex items-center gap-2 pt-2">
                <Calendar size={14} className="text-text-muted" />
                <span className="text-xs font-medium text-text-secondary">
                  {formatDate(group.meeting_date)}
                </span>
                <span className="text-xs text-text-muted">&mdash;</span>
                <Link
                  to={`/meetings/${group.series_id}/${group.meeting_instance_id}`}
                  className="text-xs text-accent hover:text-accent-hover flex items-center gap-1"
                >
                  {group.series_name} <ExternalLink size={10} />
                </Link>
              </div>

              {/* Items within this meeting */}
              {group.items.map((item) => (
                <div key={item.note_tag_id}>
                  <MeetingContentCard item={item} />
                  <AddAnnotationButton onClick={() => addAnnotation(item.note_tag_id!)} />
                  {(annotationsByPosition.get(item.note_tag_id!) ?? []).map((ann) => (
                    <AnnotationBlock
                      key={ann.id}
                      annotation={ann}
                      onUpdate={updateAnnotation}
                      onDelete={deleteAnnotation}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}

          {/* Task content */}
          {taskItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 pt-3">
                <CheckSquare size={14} className="text-text-muted" />
                <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">Tasks</span>
              </div>
              {taskItems.map((item) => (
                <TaskContentCard key={`task-${item.source_id}-${item.tag_id}`} item={item} />
              ))}
            </div>
          )}

          {/* Note content */}
          {noteItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 pt-3">
                <StickyNote size={14} className="text-text-muted" />
                <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">Notes</span>
              </div>
              {noteItems.map((item) => (
                <NoteContentCard key={`note-${item.source_id}-${item.tag_id}`} item={item} />
              ))}
            </div>
          )}
        </div>
      )}

      {showTagEditor && (
        <TagEditorModal
          currentTagIds={view.tag_ids}
          onSave={handleTagsUpdate}
          onClose={() => setShowTagEditor(false)}
        />
      )}
    </div>
    </div>
  );
}

// -- Subcomponents --

function MeetingContentCard({ item }: { item: ViewContentItem }) {
  return (
    <div className="bg-bg-card border border-border rounded-lg px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-text-primary whitespace-pre-wrap">{item.tagged_text}</p>
        </div>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
    </div>
  );
}

function TaskContentCard({ item }: { item: ViewContentItem }) {
  const statusColors: Record<string, string> = {
    todo: '#64748b',
    in_progress: '#f59e0b',
    done: '#22c55e',
    blocked: '#ef4444',
  };
  const statusColor = statusColors[item.status ?? ''] ?? '#64748b';

  return (
    <Link
      to={`/tasks/${item.source_id}`}
      className="block bg-bg-card border border-border rounded-lg px-4 py-3 hover:border-border-hover transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[10px] px-1.5 py-0.5 rounded uppercase font-medium"
              style={{ backgroundColor: statusColor + '20', color: statusColor }}
            >
              {(item.status ?? 'todo').replace('_', ' ')}
            </span>
            <span className="text-sm font-medium text-text-primary truncate">{item.title}</span>
          </div>
          {item.description && (
            <p className="text-xs text-text-muted line-clamp-2">{item.description}</p>
          )}
        </div>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
    </Link>
  );
}

function NoteContentCard({ item }: { item: ViewContentItem }) {
  return (
    <Link
      to={`/notes/${item.source_id}`}
      className="block bg-bg-card border border-border rounded-lg px-4 py-3 hover:border-border-hover transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-text-primary truncate">{item.title}</span>
          {item.updated_at && (
            <p className="text-xs text-text-muted mt-1">
              Updated {formatDate(item.updated_at)}
            </p>
          )}
        </div>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
    </Link>
  );
}

function AnnotationBlock({
  annotation,
  onUpdate,
  onDelete,
}: {
  annotation: Annotation;
  onUpdate: (id: string, content: JSONContent) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="relative group">
      <div className="border-l-2 border-accent/30 pl-3 ml-2">
        <DossierEditor
          content={annotation.content}
          onUpdate={(content) => onUpdate(annotation.id, content)}
          editable={true}
          placeholder="Write a note..."
        />
      </div>
      <button
        onClick={() => onDelete(annotation.id)}
        className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 p-1 bg-bg-surface border border-border rounded text-text-muted hover:text-danger transition-all"
        title="Remove annotation"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function AddAnnotationButton({ onClick, label }: { onClick: () => void; label?: string }) {
  return (
    <div className="flex justify-center py-1">
      <button
        onClick={onClick}
        className="flex items-center gap-1 text-xs text-text-muted hover:text-accent px-2 py-0.5 rounded hover:bg-bg-hover transition-colors opacity-0 hover:opacity-100 focus:opacity-100"
      >
        <Plus size={12} /> {label ?? 'Add note'}
      </button>
    </div>
  );
}

function TagEditorModal({
  currentTagIds,
  onSave,
  onClose,
}: {
  currentTagIds: string[];
  onSave: (tagIds: string[]) => void;
  onClose: () => void;
}) {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set(currentTagIds));
  const [loadingTags, setLoadingTags] = useState(true);

  useEffect(() => {
    apiGet<Tag[]>('/tags')
      .then(setAllTags)
      .catch(console.error)
      .finally(() => setLoadingTags(false));
  }, []);

  function toggleTag(tagId: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">Edit Tag Filters</h2>
        <p className="text-xs text-text-muted mb-3">
          Select which tags to aggregate content from.
        </p>
        {loadingTags ? (
          <div className="text-xs text-text-muted">Loading tags...</div>
        ) : allTags.length === 0 ? (
          <div className="text-xs text-text-muted">No tags available.</div>
        ) : (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {allTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => toggleTag(tag.id)}
                className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
                  selectedTagIds.has(tag.id)
                    ? 'bg-accent/10 text-text-primary'
                    : 'text-text-secondary hover:bg-bg-hover'
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="flex-1">{tag.name}</span>
                {selectedTagIds.has(tag.id) && (
                  <Check size={14} className="text-accent" />
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <button
            onClick={() => onSave(Array.from(selectedTagIds))}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// -- Helpers --

interface MeetingGroup {
  key: string;
  meeting_instance_id: string;
  meeting_date: string;
  series_name: string;
  series_id: string;
  items: ViewContentItem[];
}

function groupByMeeting(items: ViewContentItem[]): MeetingGroup[] {
  const groups = new Map<string, MeetingGroup>();
  for (const item of items) {
    const key = item.source_id;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        meeting_instance_id: item.source_id,
        meeting_date: item.meeting_date ?? '',
        series_name: item.series_name ?? '',
        series_id: item.series_id ?? '',
        items: [],
      });
    }
    groups.get(key)!.items.push(item);
  }
  return Array.from(groups.values());
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
